import { parse, ParseResult } from "papaparse";

export interface CSVRow {
  [key: string]: string;
}

export interface CSVFile {
  rows: CSVRow[];
}

const TITLE_ROW_SCAN_BYTES = 64 * 1024;
const UTF8_BOM = [0xef, 0xbb, 0xbf];
const NEWLINE = 0x0a;
const COMMA = 0x2c;

/**
 * Byte length of a leading title row — a first line with no commas above a
 * header line that has some (Numbers/Excel exports put the table name there) —
 * or 0 when the text already starts with the header row. Works on bytes, not
 * decoded text: decoding drops a BOM and swaps invalid bytes for U+FFFD, either
 * of which would shift the offset and slice into the header.
 */
function titleRowByteLength(head: Uint8Array): number {
  const bomLength = UTF8_BOM.every((byte, i) => head[i] === byte) ? 3 : 0;
  const firstNewline = head.indexOf(NEWLINE, bomLength);
  if (firstNewline === -1) {
    return 0;
  }
  const secondNewline = head.indexOf(NEWLINE, firstNewline + 1);
  const firstLine = head.subarray(bomLength, firstNewline);
  const secondLine = head.subarray(
    firstNewline + 1,
    secondNewline === -1 ? head.length : secondNewline,
  );
  const commas = (line: Uint8Array) => line.filter((b) => b === COMMA).length;
  if (commas(firstLine) > 0 || commas(secondLine) === 0) {
    return 0;
  }
  return firstNewline + 1;
}

/**
 * The file without its title row (and the BOM that precedes it), or the same
 * File when there is none. Only the first chunk is read; the result shares the
 * original's bytes.
 */
export async function stripCsvTitleRow(file: File): Promise<File> {
  const head = new Uint8Array(
    await file.slice(0, TITLE_ROW_SCAN_BYTES).arrayBuffer(),
  );
  const offset = titleRowByteLength(head);
  if (offset === 0) {
    return file;
  }
  return new File([file.slice(offset)], file.name, {
    type: file.type || "text/csv",
  });
}

export async function parseCSVHeaders(file: File): Promise<string[]> {
  const cleanFile = await stripCsvTitleRow(file);

  return new Promise((resolve, reject) => {
    parse(cleanFile, {
      header: true,
      preview: 1,
      skipEmptyLines: true,
      complete: (results: ParseResult<Record<string, string>>) => {
        resolve(results.meta.fields ?? []);
      },
      error: (error) => reject(error),
    });
  });
}

export async function processCSV(file: File): Promise<CSVFile> {
  const cleanFile = await stripCsvTitleRow(file);

  return new Promise((resolve, reject) => {
    parse(cleanFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results: ParseResult<Record<string, string>>) => {
        resolve({ rows: results.data });
      },
      error: (error) => reject(error),
    });
  });
}

/**
 * Header row exactly as the import server will see it: first non-empty record,
 * comma-delimited, no title-row stripping. Reads only the first chunk of the file.
 *
 * `step` rather than `preview: 1`: papaparse applies `preview` before it drops
 * empty lines, so a file with a leading blank line would yield no header at all.
 */
export async function parseCSVHeadersExact(file: File): Promise<string[]> {
  return new Promise((resolve, reject) => {
    let headers: string[] | undefined;
    parse<string[]>(file, {
      header: false,
      delimiter: ",",
      skipEmptyLines: true,
      step: (results, parser) => {
        headers = results.data;
        parser.abort();
      },
      complete: () => resolve(headers ?? []),
      error: (error) => reject(error),
    });
  });
}
